import { useState, type CSSProperties } from "react";
import { ApiError, type FeedbackClarificationDto, type FeedbackClarificationListResponse } from "@keydials/api-client";
import { Modal } from "./Modal";
import { Icon } from "../Icon";
import { api, getAccessToken } from "../../auth/AuthContext";
import { applyReplyLocally, markReadLocally } from "./clarifications";

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return "";
  }
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * The submitter's view of their feedback conversations: every item the team has asked a follow-up question
 * about, the exchange so far, and a box to answer in. Opening a thread marks it read, which is what clears
 * the header indicator.
 */
export function ClarificationsModal({
  state,
  onState,
  onClose,
}: {
  state: FeedbackClarificationListResponse;
  onState: (next: FeedbackClarificationListResponse) => void;
  onClose: () => void;
}) {
  const [openId, setOpenId] = useState<string | null>(() => state.items[0]?.userFeedbackId ?? null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const active = state.items.find((t) => t.userFeedbackId === openId) ?? null;

  async function openThread(thread: FeedbackClarificationDto) {
    setOpenId(thread.userFeedbackId);
    setDraft("");
    setError(null);
    if (!thread.isUnread) {
      return;
    }
    // Optimistic: the badge should clear the moment they look, not after the round-trip.
    onState(markReadLocally(state, thread.userFeedbackId));
    const token = getAccessToken();
    if (token) {
      try {
        await api.markClarificationRead(token, thread.userFeedbackId);
      } catch {
        // best-effort; the next load will correct it
      }
    }
  }

  async function send() {
    const token = getAccessToken();
    const message = draft.trim();
    if (!token || !active || !message || sending) {
      return;
    }
    setSending(true);
    setError(null);
    try {
      await api.replyToClarification(token, active.userFeedbackId, { message });
      onState(applyReplyLocally(state, active.userFeedbackId, message, new Date().toISOString()));
      setDraft("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't send your reply. Please try again.");
    } finally {
      setSending(false);
    }
  }

  if (state.items.length === 0) {
    return (
      <Modal title="Your feedback" subtitle="Replies from the team appear here." onClose={onClose}>
        <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 14.5, textAlign: "center", padding: "20px 0" }}>
          Nothing to answer right now.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title="Your feedback"
      subtitle="The team has asked for a little more detail on these."
      onClose={onClose}
      width={640}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {state.items.map((thread) => {
          const isOpen = thread.userFeedbackId === openId;
          const awaitingYou = thread.myRole === "Submitter" && thread.lastMessageBy === "Admin";
          return (
            <div key={thread.userFeedbackId} style={cardStyle(isOpen)}>
              <button onClick={() => void openThread(thread)} style={cardHeaderStyle}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  {thread.isUnread && <span style={dotStyle} />}
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: thread.isUnread ? 700 : 600,
                      color: "var(--text)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {thread.preview || thread.identifier || "Your feedback"}
                  </span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
                  {awaitingYou && <span style={pillStyle}>Needs your reply</span>}
                  <span style={{ fontSize: 12, color: "var(--text-faint)" }}>{timeAgo(thread.lastMessageAtUtc)}</span>
                  <Icon name={isOpen ? "chevDown" : "chevRight"} size={15} />
                </span>
              </button>

              {isOpen && (
                <div style={{ padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {thread.messages.length === 0 ? (
                    <p style={{ margin: 0, fontSize: 13.5, color: "var(--text-faint)" }}>
                      This conversation couldn't be loaded just now. Please try again shortly.
                    </p>
                  ) : (
                    thread.messages.map((m, i) => (
                      <div key={`${thread.userFeedbackId}-${i}`} style={bubbleStyle(m.from === "Submitter")}>
                        <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-faint)", marginBottom: 3 }}>
                          {m.from === "Submitter" ? "You" : "Key Dials team"} · {timeAgo(m.createdAtUtc)}
                        </div>
                        <div style={{ fontSize: 13.5, color: "var(--text)", whiteSpace: "pre-wrap" }}>{m.body}</div>
                      </div>
                    ))
                  )}

                  {thread.myRole === "Submitter" && (
                    <>
                      <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={3}
                        placeholder="Add the detail they asked for…"
                        style={replyFieldStyle}
                      />
                      {error && <p style={{ margin: 0, color: "var(--danger, #d64545)", fontSize: 13 }}>{error}</p>}
                      <button
                        onClick={() => void send()}
                        disabled={!draft.trim() || sending}
                        className="kd-btn-primary"
                        style={{ alignSelf: "flex-end", opacity: draft.trim() && !sending ? 1 : 0.5 }}
                      >
                        {sending ? "Sending…" : "Send reply"}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

const cardStyle = (open: boolean): CSSProperties => ({
  border: `var(--border-w) solid ${open ? "var(--accent-border)" : "var(--border)"}`,
  borderRadius: "var(--radius-sm)",
  background: "var(--surface-2)",
  overflow: "hidden",
});

const cardHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  width: "100%",
  padding: "12px 14px",
  border: "none",
  background: "none",
  font: "inherit",
  textAlign: "left",
  cursor: "pointer",
  color: "var(--text)",
};

const dotStyle: CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: 99,
  background: "var(--accent)",
  flex: "none",
};

const pillStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  padding: "2px 8px",
  borderRadius: 99,
  background: "var(--accent-soft)",
  color: "var(--accent-text)",
};

const bubbleStyle = (mine: boolean): CSSProperties => ({
  alignSelf: mine ? "flex-end" : "flex-start",
  maxWidth: "85%",
  padding: "9px 12px",
  borderRadius: "var(--radius-sm)",
  background: mine ? "var(--accent-soft)" : "var(--surface)",
  border: "var(--border-w) solid var(--border)",
});

const replyFieldStyle: CSSProperties = {
  width: "100%",
  resize: "vertical",
  font: "inherit",
  fontSize: 14,
  padding: "10px 12px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  outline: "none",
};
